import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { BarChart3, Users, Calendar, Award, TrendingUp, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_COLORS = {
  atleta: '#3b82f6',
  teacher: '#10b981',
  formatore: '#8b5cf6',
  admin: '#ef4444',
  maestro: '#f59e0b',
  contabile: '#6366f1',
};

const ROLE_LABELS = {
  atleta: 'Atleta',
  teacher: 'Insegnante',
  formatore: 'Formatore',
  admin: 'Admin',
  maestro: 'Maestro',
  contabile: 'Contabile',
};

export const AdminAnalytics = () => {
  const [overview, setOverview] = useState(null);
  const [rolesDistribution, setRolesDistribution] = useState([]);
  const [eventsParticipation, setEventsParticipation] = useState([]);
  const [registrationsTrend, setRegistrationsTrend] = useState([]);
  const [enrollmentsTrend, setEnrollmentsTrend] = useState([]);
  const [creditsTrend, setCreditsTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [ov, roles, events, regs, enrs, creds] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview`).then(r => r.data).catch(() => null),
        axios.get(`${API}/admin/analytics/roles-distribution`).then(r => r.data).catch(() => []),
        axios.get(`${API}/admin/analytics/events-participation`).then(r => r.data).catch(() => []),
        axios.get(`${API}/admin/analytics/registrations-trend`).then(r => r.data).catch(() => []),
        axios.get(`${API}/admin/analytics/enrollments-trend`).then(r => r.data).catch(() => []),
        axios.get(`${API}/admin/analytics/credits-trend`).then(r => r.data).catch(() => []),
      ]);
      setOverview(ov);
      setRolesDistribution(roles);
      setEventsParticipation(events);
      setRegistrationsTrend(regs);
      setEnrollmentsTrend(enrs);
      setCreditsTrend(creds);
    } catch {
      toast.error('Errore nel caricamento analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const maxParticipation = Math.max(...eventsParticipation.map(e => e.participants), 1);

  const formatMonth = (m) => {
    if (!m) return '';
    const [y, mo] = m.split('-');
    const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
    return `${months[parseInt(mo) - 1]} ${y}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="analytics-loading">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-500">Caricamento analytics...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Overview Cards */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="analytics-overview">
          {[
            { label: 'Utenti Totali', value: overview.total_users, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
            { label: 'Utenti Approvati', value: overview.approved_users, icon: Users, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200' },
            { label: 'Eventi', value: overview.total_events, icon: Calendar, color: 'text-violet-600', bg: 'bg-violet-50 border-violet-200' },
            { label: 'Iscrizioni', value: overview.total_enrollments, icon: TrendingUp, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
            { label: 'Certificati', value: overview.total_certificates, icon: Award, color: 'text-rose-600', bg: 'bg-rose-50 border-rose-200' },
            { label: 'Crediti Tecnici', value: overview.total_technical_credits, icon: BarChart3, color: 'text-indigo-600', bg: 'bg-indigo-50 border-indigo-200' },
          ].map((item) => (
            <div key={item.label} className={`${item.bg} border rounded-lg p-3`} data-testid={`overview-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`${item.color} text-[11px] font-medium`}>{item.label}</p>
                  <p className="text-xl font-bold mt-1">{item.value}</p>
                </div>
                <item.icon className={`w-6 h-6 ${item.color} opacity-60`} />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Roles Distribution */}
        <Card data-testid="roles-distribution-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-500" />
              Distribuzione Ruoli
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {rolesDistribution.map((r) => {
                const total = rolesDistribution.reduce((s, x) => s + x.count, 0);
                const pct = total > 0 ? ((r.count / total) * 100).toFixed(1) : 0;
                return (
                  <div key={r.role} className="flex items-center gap-3">
                    <span className="text-sm w-24 text-gray-600">{ROLE_LABELS[r.role] || r.role}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, backgroundColor: ROLE_COLORS[r.role] || '#94a3b8' }}
                      />
                    </div>
                    <span className="text-sm font-medium w-16 text-right">{r.count} ({pct}%)</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Events Participation */}
        <Card data-testid="events-participation-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-500" />
              Partecipazione Eventi (Top 10)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {eventsParticipation.length === 0 ? (
              <p className="text-center text-gray-500 py-4">Nessun dato disponibile</p>
            ) : (
              <div className="space-y-2">
                {eventsParticipation.slice(0, 10).map((e) => (
                  <div key={e.event_id} className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 w-40 truncate" title={e.event_title}>{e.event_title}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                      <div
                        className="h-full bg-violet-500 rounded-full transition-all duration-500"
                        style={{ width: `${(e.participants / maxParticipation) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium w-8 text-right">{e.participants}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Trends Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Registrations Trend */}
        <Card data-testid="registrations-trend-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-500" />
              Trend Iscrizioni Utenti
            </CardTitle>
          </CardHeader>
          <CardContent>
            {registrationsTrend.length === 0 ? (
              <p className="text-center text-gray-500 py-4 text-sm">Nessun dato</p>
            ) : (
              <div className="space-y-1">
                {registrationsTrend.slice(-12).map((r) => {
                  const maxReg = Math.max(...registrationsTrend.map(x => x.registrations), 1);
                  return (
                    <div key={r.month} className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 w-16">{formatMonth(r.month)}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${(r.registrations / maxReg) * 100}%` }} />
                      </div>
                      <span className="text-[10px] font-medium w-6 text-right">{r.registrations}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Enrollments Trend */}
        <Card data-testid="enrollments-trend-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              Trend Iscrizioni Eventi
            </CardTitle>
          </CardHeader>
          <CardContent>
            {enrollmentsTrend.length === 0 ? (
              <p className="text-center text-gray-500 py-4 text-sm">Nessun dato</p>
            ) : (
              <div className="space-y-1">
                {enrollmentsTrend.slice(-12).map((r) => {
                  const maxEnr = Math.max(...enrollmentsTrend.map(x => x.enrollments), 1);
                  return (
                    <div key={r.month} className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 w-16">{formatMonth(r.month)}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(r.enrollments / maxEnr) * 100}%` }} />
                      </div>
                      <span className="text-[10px] font-medium w-6 text-right">{r.enrollments}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Credits Trend */}
        <Card data-testid="credits-trend-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-amber-500" />
              Trend Crediti Tecnici
            </CardTitle>
          </CardHeader>
          <CardContent>
            {creditsTrend.length === 0 ? (
              <p className="text-center text-gray-500 py-4 text-sm">Nessun dato</p>
            ) : (
              <div className="space-y-1">
                {creditsTrend.slice(-12).map((r) => {
                  const maxCred = Math.max(...creditsTrend.map(x => x.total_credits), 1);
                  return (
                    <div key={r.month} className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 w-16">{formatMonth(r.month)}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                        <div className="h-full bg-amber-500 rounded-full" style={{ width: `${(r.total_credits / maxCred) * 100}%` }} />
                      </div>
                      <span className="text-[10px] font-medium w-8 text-right">{r.total_credits}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminAnalytics;
